import { useRouter } from "@core/Api/useRouter";
import type { editor } from "monaco-editor";

/** How many lines above the one the reader was sent to have to stay in view with it. */
const CONTEXT_LINES = 3;

/** Numbers the editor's lines as lines of the file and brings the reader to the one `?line=` names. */
export const useFileLines = (bodyStartLine: number) => {
	const router = useRouter();
	const asked = Number(router.query?.line);
	const requested = Number.isInteger(asked) && asked > 0 ? asked : null;

	// `line` is a one-shot hint, spent on arrival — as `useRemoveQueryT` does for `t`.
	const spend = () => {
		if (requested === null) return;
		delete router.query.line;
		router.pushQuery(router.query);
	};

	const reveal = (editor: editor.IStandaloneCodeEditor) => {
		if (requested === null) return;
		// A disposed editor has no model; nothing below is safe without one.
		const model = editor.getModel();
		if (!model) return;
		const modelLine = Math.max(1, requested - bodyStartLine + 1);
		// A selection is Monaco's own way of marking a line, with no theme of ours to keep in step.
		const column = model.getLineMaxColumn(modelLine);
		editor.setSelection({
			startLineNumber: modelLine,
			startColumn: 1,
			endLineNumber: modelLine,
			endColumn: column,
		});
		// No `setPosition` after it — Monaco would collapse the selection this just drew.
		// Centred, not pinned: the tag that opened the construct is usually above.
		editor.revealLineInCenter(modelLine);
		// Near the top of a short file centring leaves the line on the first visible
		// row and clips what was above it, so pull the view back to where it fits.
		const contextTop = editor.getTopForLineNumber(Math.max(1, modelLine - CONTEXT_LINES));
		if (editor.getScrollTop() > contextTop) editor.setScrollTop(contextTop);
		editor.focus();
		spend();
	};

	return {
		/** Monaco labels a line by what this returns; it takes the line of the buffer. */
		label: (line: number) => String(line + bodyStartLine - 1),
		reveal,
	};
};
